import { inject } from '@angular/core';
import { CanActivateFn, RedirectCommand, Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { getParentPath } from '../activated-route';
import { Languages } from '../languages';
import { iirdsStore } from '../stores/iirds.store';
import { logGuardResult } from './guard-logger';

export const localizationGuard: CanActivateFn = (route, state) => {
  const router = inject(Router);
  const iirds = inject(iirdsStore);

  if (!iirds.languages()?.length) {
    console.warn('No languages found in the iirds metadata');
    Sentry.captureEvent({
      message: `No languages found in the iirds metadata for '${iirds.cid()}'`,
      level: 'error',
    });
    iirds.setLanguage(Languages.UND);
    return logGuardResult(
      'localizationGuard',
      new RedirectCommand(
        router.createUrlTree([...getParentPath(route), Languages.UND])
      ),
      { reason: 'no languages found in iirds metadata', url: state.url }
    );
  }

  if (route.queryParamMap.has('reset')) {
    iirds.clearLanguage();
    return logGuardResult(
      'localizationGuard',
      new RedirectCommand(router.createUrlTree(getParentPath(route))),
      { reason: 'reset query param present, language cleared', url: state.url }
    );
  }

  if (iirds.hasLanguage()) {
    if (iirds.languages().some((l) => l === iirds.language())) {
      return logGuardResult(
        'localizationGuard',
        new RedirectCommand(
          router.createUrlTree([...getParentPath(route), iirds.language()])
        ),
        { reason: 'language already set and valid', language: iirds.language() }
      );
    }
  }

  if (iirds.languages().length === 1) {
    iirds.setLanguage(iirds.languages()[0]);
    return logGuardResult(
      'localizationGuard',
      new RedirectCommand(
        router.createUrlTree([...getParentPath(route), iirds.languages()[0]])
      ),
      {
        reason: 'only one language available — auto-selecting',
        language: iirds.languages()[0],
      }
    );
  }

  iirds.clearLanguage();
  return logGuardResult('localizationGuard', true, {
    reason: 'multiple languages available — showing selector',
    languages: iirds.languages(),
  });
};
